import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cognitoAuthService } from '../services/cognitoAuth';
import { apiService } from '../services/api';
import Header from '../components/Header';
import Hero from '../components/Hero';
import FAQs from '../components/FAQs';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

// User type for our app
type ExtendedUser = {
  name?: string;
  email?: string;
  subscriptionStatus?:
    | 'free'
    | 'trial'
    | 'active'
    | 'canceled'
    | 'past_due'
    | 'expired';
  trialEndDate?: string;
};

const HomePage: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation('common');
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(false);

  useEffect(() => {
    const checkAuthAndLoadUser = async () => {
      try {
        const auth = await cognitoAuthService.isAuthenticated();
        setIsAuthenticated(auth);

        if (auth) {
          setIsLoadingUser(true);
          // Fetch user profile with subscription status from API
          try {
            const userProfile = await apiService.getUserProfile();
            setUser({
              name: userProfile.name || userProfile.email,
              email: userProfile.email,
              subscriptionStatus: userProfile.subscriptionStatus || 'free',
              trialEndDate: userProfile.trialEndDate,
            });
          } catch (error) {
            console.error('Error fetching user profile:', error);
            // Fallback to basic attributes if API call fails
            const attributes = await cognitoAuthService.getUserAttributes();
            if (attributes) {
              setUser({
                name: attributes.name || attributes.email,
                email: attributes.email,
                subscriptionStatus: 'free',
                trialEndDate: undefined,
              });
            }
          } finally {
            setIsLoadingUser(false);
          }
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
      }
    };

    checkAuthAndLoadUser();
  }, []);

  const handleStartTrial = () => {
    history.push('/register');
  };

  const handleLearnMore = () => {
    history.push('/binaural-info');
  };

  const handleLogin = () => {
    history.push('/login');
  };

  const ReviewStars: React.FC = () => (
    <div className='review-stars' aria-label='5/5'>
      {[0, 1, 2, 3, 4].map((i) => (
        <svg
          key={i}
          className='review-star'
          viewBox='0 0 20 20'
          width={16}
          height={16}
          fill='currentColor'
          aria-hidden='true'
        >
          <path d='M10 1.5l2.47 5.005 5.525.803-3.998 3.896.944 5.503L10 16.115l-4.94 2.597.943-5.503L2.005 7.308l5.525-.803L10 1.5z' />
        </svg>
      ))}
    </div>
  );

  return (
    <div className='min-h-screen'>
      <SEO
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        canonical='/'
      />
      {isAuthenticated ? (
        <Header />
      ) : (
        <Header
          variant='landing'
          onLogin={handleLogin}
          onStartTrial={handleStartTrial}
        />
      )}

      <Hero onStartTrial={handleStartTrial} onLearnMore={handleLearnMore} />
      <section className='section how-it-works-wrapper background-how-it-works'>
        <div className='how-it-works-background-overlay'></div>
        <div className='how-it-works-unified'>
          <div className='how-it-works-section-content'>
            <div className='container-mobile'>
              <h2 className='subtitle'>{t('home.howItWorks')}</h2>
              <div className='process-cards-flow'>
                <div className='process-card'>
                  <span className='step-number'>1</span>
                  <div className='process-card-content'>
                    <h3>{t('home.step1Title')}</h3>
                    <p>{t('home.step1Text')}</p>
                  </div>
                  <picture>
                    <source srcSet='/images/step_1.webp' type='image/webp' />
                    <img className='step-image' src='/images/step_1.png' alt='Step 1' width={110} height={110} loading='lazy' />
                  </picture>
                </div>

                <div className='process-card'>
                  <span className='step-number'>2</span>
                  <div className='process-card-content'>
                    <h3>{t('home.step2Title')}</h3>
                    <p>{t('home.step2Text')}</p>
                  </div>
                  <picture>
                    <source srcSet='/images/step_2.webp' type='image/webp' />
                    <img className='step-image' src='/images/step_2.png' alt='Step 2' width={110} height={110} loading='lazy' />
                  </picture>
                </div>

                <div className='process-card'>
                  <span className='step-number'>3</span>
                  <div className='process-card-content'>
                    <h3>{t('home.step3Title')}</h3>
                    <p>{t('home.step3Text')}</p>
                  </div>
                  <picture>
                    <source srcSet='/images/step_3.webp' type='image/webp' />
                    <img className='step-image' src='/images/step_3.png' alt='Step 3' width={110} height={110} loading='lazy' />
                  </picture>
                </div>
              </div>

              <div className='reviews-section' style={{ marginTop: '72px' }}>
                <h2 className='subtitle' style={{ marginTop: 0 }}>
                  {t('home.reviewsTitle')}
                </h2>
                <div className='reviews-grid'>
                  {[1, 2, 3, 4].map((n) => (
                    <div className='review-card' key={n}>
                      <ReviewStars />
                      <p className='review-text'>{t(`home.review${n}Text`)}</p>
                      <div className='review-author'>
                        <img
                          className='review-avatar'
                          src={`/images/review${n}.jpg`}
                          alt={t(`home.review${n}Author`)}
                          width={44}
                          height={44}
                          loading='lazy'
                        />
                        <span className='review-author-name'>
                          {t(`home.review${n}Author`)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className='study-card'
                style={{
                  marginTop: '72px',
                  gap: '12px',
                }}
              >
                <div className='evidence-header'>
                  <h2
                    className='subtitle'
                    style={{
                      marginTop: '0',
                    }}
                  >
                    {t('home.backedByScience')}
                  </h2>
                </div>

                <div
                  style={{
                    position: 'relative',
                  }}
                >
                  <div className='home-studies-grid'>
                    <div className='study-card'>
                      <div className='study-badge'>
                        <span className='study-type'>
                          {t('home.metaAnalysis')}
                        </span>
                      </div>
                      <div className='study-header'>
                        <h4 className='study-title'>
                          Efficacy of binaural auditory beats in cognition,
                          anxiety, and pain perception: a meta-analysis
                        </h4>
                      </div>
                      <div className='study-source'>
                        <div className='source-details'>
                          <span className='source-authors'>
                            Garcia-Argibay, M., Santed, M. A., & Reales, J. M.
                          </span>
                          <span className='source-year'>2019</span>
                        </div>
                      </div>
                    </div>

                    {/* Study Card 2 */}
                    <div className='study-card hide-on-mobile'>
                      <div className='study-badge'>
                        <span className='study-type'>
                          {t('home.systematicReview')}
                        </span>
                      </div>
                      <div className='study-header'>
                        <h4 className='study-title'>
                          The Efficiency of Binaural Beats on Anxiety and
                          Depression—A Systematic Review
                        </h4>
                      </div>
                      <div className='study-source'>
                        <div className='source-details'>
                          <span className='source-authors'>
                            Baseanu, I. C. C., et al.
                          </span>
                          <span className='source-year'>2024</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Fade-out Overlay */}
                  <div className='home-studies-fade-overlay'></div>
                </div>

                <div className='process-cta-wrapper'>
                  <button
                    onClick={handleLearnMore}
                    className='btn btn-secondary'
                  >
                    {t('home.exploreStudies')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='section background-cta'>
        <div className='cta-background-overlay'></div>
        <div className='container-mobile'>
          <div className='cta-hero-wrapper'>
            <div className='cta-hero-card dark-card'>
              <h2 className='cta-hero-title'>{t('home.startTrialTitle')}</h2>
              <p className='cta-guarantee' style={{ fontSize: '13px' }}>
                <i>{t('home.noCreditCard')}</i>
              </p>

              <p
                className='cta-guarantee'
                style={{ fontSize: '13px', marginTop: '12px' }}
              >
                {t('home.andThen')}
              </p>
              <div className='cta-pricing-display'>
                <div className='cta-plan-card'>
                  <div className='cta-plan-price'>
                    <span className='price-amount'>$2.99</span>
                    <span className='price-period'>{t('home.perMonth')}</span>
                  </div>
                  <div className='cta-plan-name'>{t('home.monthlyPlan')}</div>
                </div>

                <div className='cta-plan-card featured'>
                  <div className='cta-plan-price'>
                    <span className='price-amount'>$24.99</span>
                    <span className='price-period'>{t('home.perYear')}</span>
                  </div>
                  <div className='cta-plan-name'>{t('home.annualPlan')}</div>
                  <div className='cta-plan-badge'>⭐ {t('home.save30')}</div>
                </div>
              </div>

              <button
                onClick={handleStartTrial}
                className='btn btn-primary big'
                style={{
                  marginTop: '32px',
                }}
              >
                {t('cta.startTrialFree')}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className='section' style={{ paddingBottom: '60px' }}>
        <div className='container-mobile'>
          <FAQs showTitle={true} maxItems={8} />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
